<template>
  <div :style="background" class="login">
    <CContainer class="d-flex align-items-center min-vh-100">
      <CRow class="w-100">
        <CCol col="12">
          <CCard class="p-0 m-auto">
            <CCardBody class="p-4">              
              <CRow>
                <CCol md="6" lg="6" class="pt-0 pb-0 login_form_image">
                  <img v-if="cdnBaseUrl" :src="cdnBaseUrl + '/base/dashboard/login_form_image.png'"/>
                </CCol>
                <CCol md="6" lg="6" class="pt-0 pb-0">
                  <div class="h-100 d-flex flex-column align-items-center justify-content-center">
                    <div class="w-100 mb-2 text-center">
                      <h1 class="m-0">{{ $t('login.welcome_back') }}</h1>
                    </div>
                    <div v-if="!otpRequested" class="w-100">
                      <CForm>
                        <CRow>
                          <CCol md="12" lg="12" class="pt-0 text-center">                            
                            <span v-if="!isValidEmail">{{$t('login.Enter_valid_email_to_continue')}}</span>
                            <span v-else>{{$t('login.We_will_send_otp_to_email')}}</span>
                          </CCol>
                          <CCol md="12" lg="12" class="pb-0 text-center">                            
                            <CInput class="mt-2 mb-0" :placeholder="emailFieldPlaceholder" autocomplete="username email" v-model="email" id="email" tabindex="1"></CInput>
                          </CCol>
                          <CCol md="12" lg="12" class="pb-0">
                            <CButton @click="requestOTP();" color="primary" class="w-100" tabindex="3" :disabled="!isValidEmail">
                              <span>{{$t('login.Get_otp')}}</span>
                            </CButton>
                          </CCol>
                          <CCol v-if="errorMessage" col="12" class="pb-0 text-center">
                            <p class="m-0">{{errorMessage}}</p>                        
                          </CCol>                        
                        </CRow>
                        <CRow>
                          <CCol md="12" lg="12" class="pb-0 text-center">                              
                            <span @click="errorMessage = null; otpRequested = true; loginButtonDisabled = true;" class="pointer" :class="{'link_disabled' : !isValidEmail}">{{$t('login.Already_have_an_otp')}}</span>                                 
                          </CCol>
                        </CRow>                        
                      </CForm>
                    </div>
                    <div v-else>
                      <CForm>
                        <CRow>
                          <CCol md="12" lg="12" class="pt-0 text-center">
                            <span v-html="$t('login.Enter_otp_sent_to_email', { email_address: email})"></span>
                          </CCol>
                        </CRow>
                        <CRow>
                          <CCol md="12" lg="12" class="pb-0 text-center">
                            <otpCodeInput :length="6" @input="onOtpCodeChange" @complete="onOtpCodeComplete"/>
                          </CCol>
                        </CRow>
                        <CRow>
                          <CCol md="12" lg="12" class="pb-0">
                            <CButton @click="login();" color="primary" class="w-100" :disabled="loginButtonDisabled">                              
                              <span v-if="!verifyingOtpCode">{{$t('login.Verify_and_proceed')}}</span>                              
                              <loadingSpinner v-else mode="inline" :content="$t('login.Verifying_otp')"/>
                            </CButton>
                          </CCol>
                          <CCol v-if="errorMessage" col="12" class="pb-0 text-center">
                            <p class="m-0">{{errorMessage}}</p>                        
                          </CCol>
                        </CRow>
                        <CRow>
                          <CCol md="12" lg="12" class="pb-0 text-center">                          
                            <div v-if="!showResendMessage">
                              <span>{{$t('login.No_otp_received')}}</span>
                              <span @click="requestNewOTP()" class="resend_otp">{{$t('login.Resend_otp')}}</span>
                            </div>
                            <div v-else>
                              <span>{{$t('login.New_otp_sent_to_email', { email_address: email})}}</span>
                            </div>
                          </CCol>
                        </CRow>
                        <CRow>
                          <CCol md="12" lg="12" class="pb-0 text-center">                              
                            <span @click="cancelOtp" class="pointer"><i class="fas fa-times mr-2"></i>{{$t('Cancel')}}</span>
                          </CCol>
                        </CRow>
                        <CRow v-if="environmentVariables.environment_terms_url" class="w-100 data_safety_notice">
                          <CCol md="12" lg="12" class="pb-0 text-center">
                            <a @click="openTerms()">{{$t('login.terms')}}</a>
                          </CCol>                        
                        </CRow>
                      </CForm>
                    </div>
                  </div>
                </CCol>
              </CRow>              
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>

    <div class="logo_placeholder">
      <div v-if="environmentVariables.company_id_external && environmentVariables.company_image_id">
        <img v-if="cdnBaseUrl" :src="cdnBaseUrl + '/base/dashboard/' + environmentVariables.company_id_external + '/' + environmentVariables.company_image_id + '.png'"/>
      </div>
      <div v-else-if="environmentVariables.environment_id_external">
        <img :src="getLogoSrc(environmentVariables.environment_tag)"/>
      </div>      
    </div>      
  </div>  
</template>

<script>
import axios from 'axios';
import otpCodeInput from '@/components/core/otpCodeInput.vue';
import loadingSpinner from '@/components/common/loadingSpinner.vue';

export default {
  name: 'Login',
  components: {
    otpCodeInput,
    loadingSpinner
  },
  computed: {
    isValidEmail() {
      const regex = /^[\w0-9.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return regex.test(this.email);
    }
  },
  data(){
    return {
      cdnBaseUrl: null,
      email : "",
      otpCode: "",
      copyrightText: null,
      errorMessage: null,     
      background : {
        backgroundImage: null,
        backgroundRepeat: 'no-repeat',
      },
      otpRequested: false,
      showResendMessage: false,
      loginButtonDisabled: true,
      verifyingOtpCode: false,
      emailFieldPlaceholder: null,
      platformHostname: null,
      environmentVariables: {
        company_id_external: null,
        environment_id_external: null
      },
      companyVariables: {
        company_id_external: null
      }
    }
  },
  methods: {
    getLogoSrc(environmentTag) {
      return `${this.cdnBaseUrl}/base/dashboard/logo/${environmentTag}_logo.png`;
    },
    requestNewOTP() {
      // Show the resend message
      this.showResendMessage = true;
      // Wait 1000ms and request a new OTP
      setTimeout(function() {
        this.requestOTP();
      }.bind(this), 1000);
    },
    requestOTP() {
      // Set the params
      let params = {};
      params.email = this.email;
      // Store the userEmailAddress in localStorage
      localStorage.setItem('userEmailAddress', this.email);
      // Disable the request button
      this.requestOtpButtonDisabled = true;      
      // Reset the errorMessage value
      this.errorMessage = null;
      // Request new OTP
      axios.post(process.env.VUE_APP_API_URL + '/v1/core/otp/request', params)
      .then(res => {
        // Update otpRequested value
        this.otpRequested = true;
        // Update showResendMessage value
        this.showResendMessage = false;
        // Reset the errorMessage value
        this.errorMessage = null;
        // Enable the request button
        this.requestOtpButtonDisabled = false;
      })
      .catch(err => {
        console.log(err.response.data.code);
        // Set error message when account is invalid
        if(err.response.data.code === 'Invalid user') this.errorMessage = this.$t('login.invalid_user');
      });      
    },
    cancelOtp() {
      this.otpCode = '';
      this.otpRequested = false;
      this.errorMessage = null;
    }, 
    onOtpCodeChange(data) {
      // Reset the errorMessage value
      this.errorMessage = null;      
      // Update otpCode value
      this.otpCode = data;      
    },
    onOtpCodeComplete(data) {
      // Reset the errorMessage value
      this.errorMessage = null;
      // Update otpCode value
      this.otpCode = data;
      // Enable the login button
      this.loginButtonDisabled = false;
      // Trigger the login method
      this.login();        
    },  
    login() {
      // Disable the login button
      this.loginButtonDisabled = true;
      // Start the loader
      this.verifyingOtpCode = true;      
      // Set the email and otpCode variables
      let email = this.email;
      let otpCode = this.otpCode;
                  
      setTimeout(function(){
        this.$store.dispatch('login', { email, otpCode })
        .then(res => {
          // Reset the errorMessage value
          this.errorMessage = null;
          // Get the company platform variables
          this.getCompanyVariables();
          // Set the userAuthenticationSet and authenticateUser variables
          let userAuthenticationSet = res.data.data.user_authentication_set;        
          let authenticateUser = res.data.data.authenticate_user;
          // Navigate to authentication setup page
          if(userAuthenticationSet === false) this.$router.push('/auth/setup');
          // Navigate to authentication code page
          else if(userAuthenticationSet === true && authenticateUser === true) this.$router.push('/auth/verify');
          // Navigate to dashboard homepage
          else this.$router.push('/');
          // Enable the login button
          this.loginButtonDisabled = false;
          // Stop the loader
          this.verifyingOtpCode = false;
        })
        .catch(err => {
          // Set error message when user is not authorized to login
          if(err.response.data.code === 'Not authorized') this.errorMessage = this.$t('login.error_message');
          // Set error message when license is deactivated
          else if(err.response.data.code === 'License deactivated') this.errorMessage = this.$t('login.license_deactivated');        
          // Set error message when OTP is invalid
          else if(err.response.data.code === 'Invalid OTP') this.errorMessage = this.$t('login.invalid_otp');
          // Set error message when account is invalid
          else if(err.response.data.code === 'Invalid user') this.errorMessage = this.$t('login.invalid_user');
          // Enable the login button
          this.loginButtonDisabled = false;
          // Stop the loader
          this.verifyingOtpCode = false;                
        })
      }.bind(this), 1000);
    },
    getEnvironmentVariables() {
      // Get the hostname
      this.platformHostname = window.location.hostname;
      // Set the params
      let params = {};
      params.hostname = this.platformHostname;

      axios.post(process.env.VUE_APP_API_URL + '/v1/core/platform/environments/dashboard', params)
      .then(res => {
        this.environmentVariables = res.data.data;
        // Set the dashboard title
        document.title = this.environmentVariables.dashboard_title;
        // Set the dashboard primary color
        document.documentElement.style.setProperty('--DashboardDefaultPrimary', this.environmentVariables.primary_color);        
        // Set the dashboard primary color
        document.documentElement.style.setProperty('--DashboardPrimary', this.environmentVariables.primary_color);
        // Set the dashboard secondary color
        document.documentElement.style.setProperty('--DashboardSecondary', this.environmentVariables.secondary_color);
        // Set the dashboard font
        document.documentElement.style.setProperty('--DashboardFontFamily', this.environmentVariables.font_family);
        // Set the background image
        this.background.backgroundImage = `url(${this.cdnBaseUrl}/base/dashboard/login_background_image.png)`;
        // Set the copyright text
        this.copyrightText = this.environmentVariables.copyright;        
        // Set the favicon href
        document.querySelector("link[rel~='icon']").href = `${this.cdnBaseUrl}/base/dashboard/logo/${this.environmentVariables.environment_tag}_favicon.png`;
        // Set the placeholder for the email field
        this.emailFieldPlaceholder = "📩 " + this.environmentVariables.environment_contact_email;
      })
      .catch(err => {
        console.error(err); 
      });      
    },
    getCompanyVariables() {
      axios.get(process.env.VUE_APP_API_URL + '/v1/core/platform/variables/dashboard/company')
      .then(res => {
        this.companyVariables = res.data.data;
        // Set the dashboard title
        document.title = this.companyVariables.dashboard_title;
        // Set the dashboard primary color
        document.documentElement.style.setProperty('--DashboardDefaultPrimary', this.companyVariables.environment_primary_color);        
        // Set default color variables
        let primaryColor = null;
        let secondaryColor = null;
        // Set the primaryColor value
        (this.companyVariables.primary_color) ? primaryColor = this.companyVariables.primary_color : primaryColor = this.companyVariables.environment_primary_color;
        // Set the dashboard primary color
        document.documentElement.style.setProperty('--DashboardPrimary', primaryColor);
        // Set the primaryColor value
        (this.companyVariables.secondary_color) ? secondaryColor = this.companyVariables.secondary_color : secondaryColor = this.companyVariables.environment_secondary_color;
        // Set the dashboard secondary color
        document.documentElement.style.setProperty('--DashboardSecondary', secondaryColor);
        // Set the dashboard font
        document.documentElement.style.setProperty('--DashboardFontFamily', this.companyVariables.font_family);        
      })
      .catch(err => {
        console.error(err); 
      });
    },
    openTerms() {
      var win = window.open(this.environmentVariables.environment_terms_url, '_blank');
      win.focus();      
    }
  },
  mounted: function() {
    // Set the base URL for the CDN
    this.cdnBaseUrl = process.env.VUE_APP_CDN_URL;
    // Check if the userEmailAddress is available in localStorage
    if(localStorage.getItem('userEmailAddress') !== null) this.email = localStorage.getItem('userEmailAddress');
    // Get the default platform variables
    this.getEnvironmentVariables();
  }
}
</script>
